import { toast } from '@music-vine/cadence/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { v1Api } from '@/api/backendApi';
import { systemKeys } from '@/utils/queryKeys';

// Long-open tabs are the ones that go stale, so poll rather than only checking
// on load. Refetches pause while the tab is hidden.
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

const NEW_VERSION_TOAST_ID = 'new-version-available';

interface LatestVersionResponse {
  sha: string;
}

async function fetchLatestVersion(): Promise<string> {
  // Must bypass the CDN, otherwise a cached SHA hides the new deploy.
  const { data } = await v1Api.get<LatestVersionResponse>(
    'page/latest_version',
    { headers: { 'X-Skip-CDN': 'true' } }
  );

  return data.sha;
}

export const isNewVersionAvailable = (
  currentVersion: string | undefined,
  latestVersion: string | undefined
): boolean =>
  Boolean(currentVersion && latestVersion && currentVersion !== latestVersion);

/**
 * Prompts the user to refresh when a newer production build has been deployed.
 *
 * PageApi stores the commit SHA of each production deploy (via a Vercel
 * `deployment.succeeded` webhook) and serves it from `page/latest_version`.
 * We compare that with the SHA this bundle was built from.
 *
 * Only enabled in production: the webhook only records production deploys, so
 * preview/preprod builds would never match and would always show the prompt.
 */
export function useNewVersionCheck() {
  const currentVersion = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;
  const enabled =
    process.env.NEXT_PUBLIC_ENV === 'production' && Boolean(currentVersion);

  const { data: latestVersion } = useQuery({
    queryKey: systemKeys.versionCheck(),
    queryFn: fetchLatestVersion,
    enabled,
    refetchInterval: VERSION_POLL_INTERVAL_MS,
    // Also re-checks when a tab is refocused, but no more than once per interval.
    staleTime: VERSION_POLL_INTERVAL_MS,
    retry: false,
  });

  useEffect(() => {
    if (!isNewVersionAvailable(currentVersion, latestVersion)) {
      return;
    }

    // Fixed id so repeat polls don't stack toasts. If the user dismisses it,
    // it only comes back once a further deploy changes the SHA again.
    toast.info({
      id: NEW_VERSION_TOAST_ID,
      title: 'New version available',
      description: 'Refresh the page to get the latest version of Uppbeat.',
      action: {
        label: 'Refresh',
        onClick: () => window.location.reload(),
      },
      duration: Number.POSITIVE_INFINITY,
    });
  }, [currentVersion, latestVersion]);
}
